import PropTypes from 'prop-types';
import React, { lazy } from 'react';
import { Field } from 'formik';
import { Link as LinkLi } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import useTheme from '@material-ui/core/styles/useTheme';
import { Link } from 'react-router-dom';
import { pageUrls } from 'globalconfig/constants/keys';
import MicrosoftLoginButton from './components/MicrosoftLoginButton';

// Dynamic imports based on environment layout
const layout = window.$environment?.CURRENT_LAYOUT;
const serviceProvider = window.$environment?.SERVICE_PROVIDER;

const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const CheckBox = lazy(() => import(`globalcomponents/UI/Toggle/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const LinkTo = lazy(() => import(`globalcomponents/UI/Link`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const SubHeader = lazy(() => import(`globalcomponents/SubHeader/${layout}`));

const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`).default;

const RenderComponent = ({
  values,
  handleEdit,
  errors,
  isNotValid,
  redirectedFromFleetops,
  redirectedUrl,
  validateForm,
  submitAction,
  customProps: { i18n, Styled, history, hasLoginApiStatus, hasMicrosoftLoginApiStatus, hasRegistration, fetchMicrosoftLogin, webAppConfigurations },
}) => {
  const theme = useTheme();

  const renderRegistrationLink = () => {
    if (!hasRegistration) return null;

    const registrationText = i18n.t('REGISTRATION');

    return (
      <h1 className="registrationHeader">
        <div className="linkWrap">
          {serviceProvider === 'gs' && (
            <LinkLi component={Link} to={pageUrls.registration} className="registrationHeaderlink">
              {registrationText}
            </LinkLi>
          )}
          {serviceProvider === 'trackon' && (
            <a className="registrationHeaderlink" href={window.$environment?.REG_LINK}>
              {registrationText}
            </a>
          )}
        </div>
      </h1>
    );
  };


  return (
    <>
      <Styled.LoginHeader style={{ ...theme.palette.registrationtrackon }}>
        <SubHeader variant="h1" text={i18n.t('LOG IN')} mode="light" className="loginHeader" />
        {renderRegistrationLink()}
      </Styled.LoginHeader>

      {(hasMicrosoftLoginApiStatus?.errorMessage || hasLoginApiStatus?.errorMessage) && (
        <Styled.LoginError>
          <Message
            type="error"
            className="errorMessage"
            message={hasMicrosoftLoginApiStatus?.errorMessage || hasLoginApiStatus?.errorMessage}
          />
        </Styled.LoginError>
      )}

      {/* Username */}
      <Styled.userNameWrapper>
        <FormStyle.FieldsWrapper width="100%" align="end" justify="center">
          <Label mode="light" className="labelSize">
            {i18n.t('Email')}
          </Label>
          <FormStyle.TextWrapper mode="light">
            <Field
              name="username"
              as={Input}
              mode="light"
              isNotDebounced
              placeholder={i18n.t('Email or Employee Id')}
              type="text"
              value={values.username}
              handleOnChange={(event) => handleEdit(event, { field: 'username', type: 'username' })}
            />
            {errors.username && !isNotValid && <Message type="error" message={errors.username} />}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
      </Styled.userNameWrapper>

      {/* Password */}
      <Styled.passwordWrapper>
        <FormStyle.FieldsWrapper width="100%" align="end" justify="center">
          <Label mode="light">{i18n.t('common.password')}</Label>
          <FormStyle.TextWrapper>
            <Field
              name="password"
              as={Input}
              mode="light"
              isNotDebounced
              placeholder={i18n.t('common.password')}
              type="password"
              value={values.password}
              handleOnChange={(event) => handleEdit(event, { field: 'password', type: 'password' })}
            />
            {errors.password && !isNotValid && <Message type="error" message={errors.password} />}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
      </Styled.passwordWrapper>

      {/* Password Help + Remember Me */}
      <Styled.PasswordHandler>
        <LinkTo href={pageUrls.recovery}>{i18n.t('Forgot password?')}</LinkTo>
        <CheckBox
          type="NormalWhite"
          className="remember"
          checked={values.rememberMe}
          label={i18n.t('Remember me')}
          onClick={(event) => handleEdit(event, { field: 'rememberMe', type: 'checkbox' })}
        />
      </Styled.PasswordHandler>

      {/* Submit Button */}
      <Styled.LoginButtonWrapper>
        <FormStyle.ButtonsWrapper>
          <Button
            type="submit"
            label={hasLoginApiStatus?.isLoading ? <CircularProgress color="inherit" /> : i18n.t('Log in')}
            onClick={() => {
              const validateResponse = validateForm();
              validateResponse.then((data) => submitAction(data));
            }}
          />
        </FormStyle.ButtonsWrapper>
      </Styled.LoginButtonWrapper>

      {/* Microsoft SSO Login */}
      <MicrosoftLoginButton
        history={history}
        fetchMicrosoftLogin={fetchMicrosoftLogin}
        i18n={i18n}
        redirectedFromFleetops={redirectedFromFleetops}
        redirectedUrl={redirectedUrl}
        hasMicrosoftLoginApiStatus={hasMicrosoftLoginApiStatus}
        Styled={Styled}
        webAppConfigurations={webAppConfigurations}
      />
    </>
  );
};

RenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNotValid: PropTypes.bool.isRequired,
  validateForm: PropTypes.func.isRequired,
  submitAction: PropTypes.func.isRequired,
  customProps: PropTypes.shape({
    i18n: PropTypes.object.isRequired,
    Styled: PropTypes.object.isRequired,
    history: PropTypes.object,
    hasLoginApiStatus: PropTypes.object,
    hasMicrosoftLoginApiStatus: PropTypes.object,
    hasRegistration: PropTypes.bool,
    fetchLogin: PropTypes.func,
    fetchMicrosoftLogin: PropTypes.func,
    webAppConfigurations: PropTypes.object,
  }).isRequired,
};

export default RenderComponent;
